import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, X, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LABELS = {
  en: {
    title: "Getting started",
    subtitle: "Complete these steps to get the most out of The Archive",
    searched: "Search a document",
    downloaded: "Download a document",
    saved_to_collection: "Save an item to a collection",
    submitted: "Submit your first work",
    dismiss: "Hide this checklist",
    of: "of",
    done: "done",
  },
  fr: {
    title: "Bien démarrer",
    subtitle: "Complétez ces étapes pour profiter pleinement de The Archive",
    searched: "Rechercher un document",
    downloaded: "Télécharger un document",
    saved_to_collection: "Enregistrer un document dans une collection",
    submitted: "Soumettre votre premier travail",
    dismiss: "Masquer cette liste",
    of: "sur",
    done: "terminées",
  },
};

const STEP_LINKS = {
  searched: "/search",
  downloaded: "/search",
  saved_to_collection: "/collections",
  submitted: "/upload",
};

const STEP_ORDER = ["searched", "downloaded", "saved_to_collection", "submitted"];

export const OnboardingChecklist = ({ user, lang = "en" }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const t = LABELS[lang] || LABELS.en;

  useEffect(() => {
    if (!user || user.role === "admin") return;
    const run = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;
        const resp = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(resp.data);
      } catch (e) {
        // silently ignore
      }
    };
    run();
  }, [user]);

  if (!user || user.role === "admin" || !status || status.dismissed || status.completed) {
    return null;
  }

  const steps = status.steps || {};
  const doneCount = STEP_ORDER.filter((k) => steps[k]).length;

  const dismiss = async () => {
    setStatus({ ...status, dismissed: true });
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/onboarding/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // silently ignore
    }
  };

  return (
    <section className="max-w-4xl mx-auto px-6 md:px-12 py-8" data-testid="onboarding-checklist">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 relative">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          title={t.dismiss}
          data-testid="onboarding-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-baseline gap-3 mb-1">
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>
            {t.title}
          </h3>
          <span className="text-xs font-medium text-orange-600" data-testid="onboarding-progress">
            {doneCount} {t.of} {STEP_ORDER.length} {t.done}
          </span>
        </div>
        <p className="text-xs text-slate-500 mb-4">{t.subtitle}</p>
        <div className="h-1.5 bg-slate-100 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-orange-500 rounded-full transition-all"
            style={{ width: `${(doneCount / STEP_ORDER.length) * 100}%` }}
          />
        </div>
        <ul className="space-y-2">
          {STEP_ORDER.map((key) => {
            const done = !!steps[key];
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => !done && navigate(STEP_LINKS[key])}
                  disabled={done}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                    done
                      ? "text-slate-400 line-through cursor-default"
                      : "text-slate-700 hover:bg-orange-50 group"
                  }`}
                  data-testid={`onboarding-step-${key}`}
                >
                  {done ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-300 shrink-0" />
                  )}
                  <span className="flex-1">{t[key]}</span>
                  {!done && (
                    <ArrowRight className="w-4 h-4 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default OnboardingChecklist;
